import type { FieldTypes } from "@circle-react-shared/ProfileFields/constants";
import { FIELD_TYPES } from "@circle-react-shared/ProfileFields/constants";
import { nanoid } from "nanoid";
import type { ComponentType } from "react";

import type { PlanTiers } from "@/react/helpers/planHelpers";

interface FilterUpgradeDetails {
  minimumRequiredTier: PlanTiers;
}

/** Configration object used to render the filter on UI */
export interface RenderFilter {
  /** @TODO component prop type to be updated when API is stable. */
  component: ComponentType<any>;
  configuration?: unknown;
  label: string;
  /** If the list of filters requires a different label, use this key. */
  listLabel?: string;
  name: string;
  upgradeDetails?: FilterUpgradeDetails;
}

export interface RenderProfileFieldFilter extends RenderFilter {
  field_type: FieldTypes;
  id: number;
}

export const isRenderProfileFieldFilter = (
  renderFilterParam: RenderFilter,
): renderFilterParam is RenderProfileFieldFilter =>
  "id" in renderFilterParam && "field_type" in renderFilterParam;

/** Communication with API starts with this shape. Specialize this with types for field type and value  */
export interface BaseFilter {
  id: string;
  /** When a filter is added, automatically open it. */
  isInitialOpen?: boolean;
  key: string;
  value?: unknown;
}
export interface StringValueFilter extends BaseFilter {
  value?: string;
}

export interface ProfileFieldBaseFilter extends BaseFilter {
  key: "profile_field";
  profile_field_id: number;
  profile_field_type: FieldTypes;
}

export interface GeoLocationFilter extends BaseFilter {
  key: "geo_location";
  value: {
    id: string;
    name: string;
    source: string;
  };
}

export const createBaseFilter = <T extends BaseFilter>(
  props: Omit<T, "id"> & { id?: string },
) => ({
  id: props.id ?? nanoid(),
  ...props,
});

const isProfileFieldType = (
  profileFieldType: unknown,
): profileFieldType is FieldTypes =>
  Object.values(FIELD_TYPES).includes(profileFieldType as FieldTypes);

export const isProfileFieldBaseFilter = (
  baseFilter: BaseFilter,
): baseFilter is ProfileFieldBaseFilter =>
  baseFilter.key === "profile_field" &&
  "profile_field_id" in baseFilter &&
  "profile_field_type" in baseFilter &&
  isProfileFieldType(baseFilter.profile_field_type);
